import React, { useState } from "react";
import Logo from "../../Images/Logo-T.png";
import "../Styles/Login.css";
import AuthError from "../../Popups/Auth/Errors/AuthError";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { FaEye, FaEyeSlash } from "react-icons/fa";

import { auth, usersDB } from "../../Config/Firebase";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";

import { useNavigate } from "react-router-dom";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const [error, setError] = useState("");

  const navigate = useNavigate();

  // ✅ Input validation
  const validateInputs = () => {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail || !normalizedEmail.includes("@")) {
      return "אימייל לא תקין";
    }

    if (!password || password.length < 6) {
      return "הסיסמה חייבת להיות לפחות 6 תווים";
    }

    return null;
  };

  // 🔐 EMAIL LOGIN
  const handleLogin = async (e) => {
    e.preventDefault();

    if (loading) return;

    setError("");
    setLoading(true);

    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();

    try {
      await signInWithEmailAndPassword(auth, normalizedEmail, password);
      navigate("/dashboard");
    } catch (err) {
      console.error("LOGIN ERROR:", err);
      setError(getHebrewError(err.code || ""));
    }

    setLoading(false);
  };

  // 🔵 GOOGLE LOGIN
  const handleGoogleLogin = async () => {
    if (googleLoading) return;

    setError("");
    setGoogleLoading(true);

    const provider = new GoogleAuthProvider();

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const userRef = doc(usersDB, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        const providerData = user.providerData?.[0] || {};

        await setDoc(userRef, {
          uid: user.uid,
          email: user.email || "",
          name: user.displayName || "",
          photo: user.photoURL || "",

          provider: providerData.providerId || "google",
          providerUid: providerData.uid || "",

          phone: user.phoneNumber || "",
          emailVerified: user.emailVerified || false,

          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),

          isProfileComplete: false,
        });

        navigate("/register");
      } else {
        await setDoc(
          userRef,
          {
            lastLogin: serverTimestamp(),
          },
          { merge: true }
        );

        navigate("/dashboard");
      }
    } catch (err) {
      console.error("GOOGLE LOGIN ERROR:", err);
      setError(getHebrewError(err.code || ""));
    }

    setGoogleLoading(false);
  };

  // ❗ ERROR TRANSLATION
  const getHebrewError = (code) => {
    switch (code) {
      case "auth/user-not-found":
        return "המשתמש לא קיים";

      case "auth/wrong-password":
        return "סיסמה שגויה";

      case "auth/invalid-email":
        return "אימייל לא תקין";

      case "auth/user-disabled":
        return "החשבון נחסם";

      case "auth/too-many-requests":
        return "יותר מדי ניסיונות, נסה שוב מאוחר יותר";

      case "auth/network-request-failed":
        return "בעיה בחיבור לאינטרנט";

      case "auth/internal-error":
        return "שגיאה פנימית, נסה שוב";

      case "auth/email-already-in-use":
        return "האימייל כבר בשימוש";

      case "auth/weak-password":
        return "הסיסמה חלשה מדי";

      case "auth/popup-closed-by-user":
        return "החלון נסגר לפני השלמת ההתחברות";

      case "auth/popup-blocked":
        return "הדפדפן חסם את חלון ההתחברות";

      case "auth/account-exists-with-different-credential":
        return "החשבון קיים עם שיטת התחברות אחרת";

      case "auth/invalid-credential":
        return "פרטי ההתחברות שגויים";

      default:
        return "שגיאה בהתחברות, נסה שוב";
    }
  };

  return (
    <div className="login-container" dir="rtl">
      <div className="login-card">

        <div className="login-logo-wrapper">
          <img src={Logo} alt="Logo" />
        </div>

        <h1 className="login-title">התחברות</h1>
        <p className="login-subtitle">כניסה לחשבון שלך</p>

        <form onSubmit={handleLogin} className="login-form">

          {/* EMAIL */}
          <div className="login-input-group">
            <label>אימייל</label>
            <input
              className="login-input"
              type="email"
              placeholder="example@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading || googleLoading}
              required
            />
          </div>
          {/* PASSWORD */}
          <div className="login-input-group">
            <label htmlFor="password">סיסמה</label>
          
            <div className="login-password-field">
              <input
                id="password"
                className="login-input"
                type={showPassword ? "text" : "password"}
                placeholder="הכנס סיסמה"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading || googleLoading}
                autoComplete="current-password"
                required
              />
          
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "הסתר סיסמה" : "הצג סיסמה"}
                tabIndex={0}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {/* LOGIN BUTTON */}
          <button
            className="login-primary-btn"
            type="submit"
            disabled={loading || googleLoading}
          >
            {loading ? "מתחבר..." : "התחבר"}
          </button>

        </form>

        <div className="login-divider">
          <span>או</span>
        </div>

        {/* GOOGLE BUTTON */}
        <button
          className="login-google-btn"
          onClick={handleGoogleLogin}
          disabled={loading || googleLoading}
        >
          <img
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            alt="Google"
          />
          <span>
            {googleLoading ? "מתחבר..." : "המשך עם Google"}
          </span>
        </button>

        <p className="login-bottom-text">
          אין לך חשבון?{" "}
          <span onClick={() => navigate("/register")}>צור חשבון</span>
        </p>

      </div>

      {/* ERROR POPUP */}
      {error && (
        <AuthError
          message={error}
          onClose={() => setError("")}
        />
      )}
    </div>
  );
}

export default Login;